import { OnlineEvaluationLayoutComponent } from './shared/components/layouts/online-evaluation-layout/online-evaluation-layout.component';
import { NgModule } from '@angular/core';
import { Routes, RouterModule, RouteReuseStrategy } from '@angular/router';
import { AppRouteReuseStrategy } from '@app-shared/strategies/app-route-reuse.strategy';
import { AuthLayoutComponent } from './shared/components/layouts/auth-layout/auth-layout.component';
import { SessionGuard } from './shared/guards/session-auth.guard';
import { BlankLayoutComponent } from './shared/components/layouts/blank-layout/blank-layout.component';
import { RoleGuard } from '@app-shared/guards/roles.guard';
import { Permission, Role, SYSTEM_ADMIN_PERMISSIONS, OEM_DEALER_ADMIN_PERMISSIONS, BUY_PERMISSIONS, SELL_PERMISSIONS, LEGACY_SELL_ROUTE_ROLES, LEGACY_BUY_ROUTE_ROLES, LEGACY_EVAL_DASHBOARD_ROLES, LEGACY_TRADEIN_DASHBOARD_ROLES, LEGACY_ADMIN_ROLES } from './store/session/session.store';
import { ShopkeeperLayoutComponent } from '@app-components/layouts/shopkeeper-layout/shopkeeper-layout.component';
import { PluginRaioXLayoutComponent } from '@app-components/layouts/plugin-raio-x-layout/plugin-raio-x-layout.component';
import { ShareComponentComponent } from './views/public/share/share-component/share-component.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'arrematar/home',
    pathMatch: 'full',
  },
  {
    path: 'online-eval',
    component: OnlineEvaluationLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './views/public/online-evaluation/online-evaluation.module'
          ).then((m) => m.OnlineEvaluationModule),
      },
    ],
  },
  {
    path: 'plugin-raiox',
    component: PluginRaioXLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './views/public/plugin-raio-x/plugin-raio-x.module'
          ).then((m) => m.PluginRaioXModule),
      },
    ],
  },
  {
    path: 'raio-x',
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './views/public/raio-x-download/raio-x-download.module'
          ).then((m) => m.RaioXDownloadModule),
      },
    ],
  },
  {
    path: 'dashboard/eval',
    component: ShopkeeperLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './views/private/eval-dashboard/eval-dashboard.module'
          ).then((m) => m.EvalDashboardModule),
        canActivate: [SessionGuard, RoleGuard],
        data: {
          roles: [...LEGACY_EVAL_DASHBOARD_ROLES],
          permissions: [
            Permission.GLOBAL_ACCESS,
            Permission.DASHBOARD_MAIN_PAGE,
            Permission.VIEW_EVALUATION_DASHBOARD,
          ]
        },
      },
    ],
  },
  {
    path: 'dashboard/tradein',
    component: ShopkeeperLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './views/private/tradein-dashboard/tradein-dashboard.module'
          ).then((m) => m.TradeInDashboardModule),
        canActivate: [SessionGuard, RoleGuard],
        data: {
          roles: [...LEGACY_TRADEIN_DASHBOARD_ROLES],
          permissions: [
            Permission.GLOBAL_ACCESS,
            Permission.VIEW_TRADEIN_DASHBOARD,
          ]
        },
      },
    ],
  },
  {
    path: 'dashboard/lead',
    component: ShopkeeperLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './views/private/lead-dashboard/lead-dashboard.module'
          ).then((m) => m.LeadDashboardModule),
        canActivate: [SessionGuard, RoleGuard],
        data: {
          roles: [...LEGACY_EVAL_DASHBOARD_ROLES],
          permissions: [
            Permission.GLOBAL_ACCESS,
            Permission.DASHBOARD_MAIN_PAGE,
            Permission.VIEW_LEADS_VEHICLES,
          ],
        },
      },
    ],
  },
  {
    path: 'dashboard/analise-comparativa',
    component: ShopkeeperLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './views/private/comparative-analysis/comparative-analysis.component'
          ).then((m) => m.ComparativeAnalysisComponent),
        canActivate: [SessionGuard, RoleGuard],
        data: {
          roles: [...LEGACY_EVAL_DASHBOARD_ROLES],
          permissions: [
            Permission.GLOBAL_ACCESS,
            Permission.VIEW_EVALUATION_DETAILS,
            Permission.VIEW_EVALUATION_DASHBOARD,
            Permission.DOWNLOAD_EVALUATION_REPORTS,
          ],
        },
      }
    ]
  },
  {
    path: 'relatorio/avaliacoes',
    component: ShopkeeperLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import(
            './views/private/evals-report/evals-report.module'
          ).then((m) => m.EvalsReportModule),
        canActivate: [SessionGuard, RoleGuard],
        data: {
          roles: [...LEGACY_EVAL_DASHBOARD_ROLES],
          permissions: [
            Permission.GLOBAL_ACCESS,
            Permission.VIEW_EVALUATION_DETAILS,
            Permission.DOWNLOAD_EVALUATION_REPORTS,
          ],
        },
      },
    ],
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./views/public/public.module').then((m) => {
            return m.PublicModule;
          }),
      },
    ],
  },
  {
    path: 'compartilhar/:type/veiculos/:slug/:id',
    component: ShareComponentComponent,
  },
  {
    path: '',
    loadChildren: () =>
      import('./views/private/shopkeeper/shopkeeper.module').then(
        (m) => m.ShopkeeperModule
      ),
    data: {
      roles: [...LEGACY_SELL_ROUTE_ROLES],
      permissions: [
        ...SELL_PERMISSIONS,
        ...OEM_DEALER_ADMIN_PERMISSIONS,
      ]
    },
  },
  {
    path: '',
    loadChildren: () =>
      import('./views/private/evaluation/evaluation.module').then(
        (m) => m.EvaluationModule
      ),
    canActivate: [SessionGuard, RoleGuard],
    data: {
      roles: [...LEGACY_BUY_ROUTE_ROLES],
      permissions: [
        ...BUY_PERMISSIONS,
        ...OEM_DEALER_ADMIN_PERMISSIONS,
      ]
    },
  },
  {
    path: 'admin',
    loadChildren: () =>
      import('./views/private/admin/admin.module').then((m) => m.AdminModule),
    canActivate: [SessionGuard, RoleGuard],
    data: {
      roles: [...LEGACY_ADMIN_ROLES],
      permissions: [
        Permission.GLOBAL_ACCESS,
        ...SYSTEM_ADMIN_PERMISSIONS,
      ],
    },
  },
  {
    path: '',
    component: BlankLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./views/others/others.module').then((m) => m.OthersModule),
      },
    ],
  },
  {
    path: 'compradores',
    loadChildren: () => import('./views/public/dealers/dealers.module').then(m => m.DealersModule)
  },
  {
    path: 'alpes-one',
    loadChildren: () =>
      import('./views/integrations/integrations.module').then((m) => m.IntegrationsModule)
    ,
    canActivate: [SessionGuard]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
  providers: [{ provide: RouteReuseStrategy, useExisting: AppRouteReuseStrategy }],
})
export class AppRoutingModule { }
